import { Component, OnInit, Input } from '@angular/core';
import { ColumnChartConfig } from '../../../../../shared/google-chart/Models/ColumnChartConfig';
import { ChartUtil } from '../../../../../shared/services/utility/chart.service';
import { ngxCsv } from 'ngx-csv/ngx-csv';
import * as moment from 'moment';
import { CommonUtil } from '../../../../../shared/services/utility/common.service';

@Component({
  selector: 'app-charger-assets-utilization-new',
  templateUrl: './charger-assets-utilization-new.component.html',
  styleUrls: ['./charger-assets-utilization-new.component.css']
})
export class ChargerAssetsUtilizationNewComponent implements OnInit {
	@Input() data: any;
	@Input() siteId: number = 0;
	@Input() customerId: number = 0;
	@Input() siteBattviewsData: any = {};
	@Input() siteTags: any = {};
	@Input() siteBattviewsTags: any = {};
	@Input() customerName: string = '';
	@Input() siteName: string = '';

	// Add the selectedPeriod property
	selectedPeriod: string = '7'; // Default to 7 days
	chartData: any = { list: 0 };  // Declare chartData as class property

	config: ColumnChartConfig;
	filteredData: any;
	tableColumns: any[] = [
		{title: "Charger ID", property: "chargerusername"},
		{title: "Charger SN", property: "serialnumber"},
		{title: "Value", property: "value"}
	];
	title: string = 'Utilization';
	tooltipText: string = 'The percentage of chargers being utilized by plug ins is displayed. Calculated by the Expected Number of Cycles Delivered, the selected charger (Fast, Conventional or Opportunity) and the Site Weekly Working Days Count can be filtered based on the selected time frame, charger profile and charger model.';
	elementId: string = 'charger_assets_utilization';
	widgetClass: string = 'auto-height-widget';
	siteWidgetOptions: any = {
		hasOperation: false,
		hasChargerModels: false,
		hasNormalizeCheckBox: false,
		hasDate: true,
		isSitePerformanceWidget: true,
		isChargers: true,
		excludeSinceInstallationOption: true,
		hideYesterdayDate: true,
		isCircleWidget: true
	};

	constructor(private chartUtil: ChartUtil, private commonUtil: CommonUtil) { }

	ngOnInit() {
		this.initializeConfig();

		// Initialize filteredData and chartData if needed
		if (!this.filteredData && this.data) {
			this.filteredData = {
				list: this.data.list || []
			};
			const utilization = this.calculateUtilization();
			this.chartData = { list: utilization };
		}
	}

	private initializeConfig() {
		this.config = new ColumnChartConfig({
			isPercent: true,
			xTitle: "% Utilization",
			yTitle: "% Chargers",
			widgetHeight: 400,
			chartArea: {top: 10, width:'70%', height:'80%'},
			vAxisMinValue: 0,
			tooltip: {trigger: 'hover'}
		});

		this.config.formatFunction = (data) => {
			data = data.list;

			if(!data) return {list: 0}

			let totalValue = 0;
			// let dependency = 550;  //(10% * 100%) + (20% * 100%) + (30% * 100%) + ... + (90% * 100%) + (100% * 100%) = 550%
			// let dependency = 225;  //(10% * 100%) + (20% * 100%) + (30% * 100%) + ... + (90% * 100%) + (100% * 100%) = 225%

      		let dependency = 100;  // Set to 100 to achieve approximately 99% utilization

			for(let i = 0; i < data.length; i++) {
			 let item: any = data[i];

			 totalValue += item.x * item.y;   //accumulated value(Y %charger with X %utilization) = utilization at item.X% * charger amount at item.Y%
			}

			return {list: totalValue / dependency}   //total weighted / ideal full weight value (550% utilization with 100% charger being used)
		};
	}

	private calculateUtilization(): number {
		const period = this.selectedPeriod || '7';

		console.log('=== Calculating Utilization ===');
		console.log('Period:', period);
		console.log('Full data object:', this.data);

		// Check if required data structures exist
		if (!this.data?.count_of_cycles || !this.data?.plugins) {
			console.warn('Missing required data structures for utilization calculation');
			return 0;
		}

		// Check if period data exists
		if (!this.data.count_of_cycles[period] || !this.data.plugins[period]) {
			console.warn(`No data available for period: ${period}`);
			return 0;
		}

		console.log('Count of cycles data:', this.data.count_of_cycles[period]);
		console.log('Plugins data:', this.data.plugins[period]);

		// Sum up actual cycles from count_of_cycles
		const actualCycles = this.data.count_of_cycles[period].reduce((sum, item) => {
			return sum + (item.count || 0);
		}, 0);

		// Sum up expected cycles from plugins
		const expectedCycles = Object.values(this.data.plugins[period])
			.reduce((sum: number, chargerData: Record<string, number>) => {
				return sum + Object.values(chargerData).reduce((chargerSum: number, count: number) => {
					return chargerSum + (Number(count) || 0);
				}, 0);
			}, 0);

		console.log('Actual cycles:', actualCycles);
		console.log('Expected cycles:', expectedCycles);

		// Calculate adjusted days based on period
		let adjustedDays: number;
		switch (period) {
			case 'Y':
				adjustedDays = 1;
				break;
			case 'W':
				adjustedDays = 7;
				break;
			default:
				adjustedDays = parseInt(period, 10);
				break;
		}

		console.log('Adjusted days:', adjustedDays);

		// If expected cycles is 0, return 0 to avoid division by zero
		if (expectedCycles === 0 || adjustedDays === 0) {
			return 0;
		}

	// Calculate utilization with explicit number conversions
	// The utilization should be calculated as the percentage of actual cycles compared to expected cycles per day
	// This matches the calculation in the generateChargersPerformanceWidgetData method in chart-data.service.ts
	let utilization: number;
	if (period === 'Y') {
		// For yesterday, just calculate the percentage
		utilization = (Number(actualCycles) / Number(expectedCycles)) * 100;
	} else {
		// For other periods, divide the expected cycles by the number of days
		// This is equivalent to calculating the utilization per day
		utilization = (Number(actualCycles) / (Number(expectedCycles) / adjustedDays)) * 100;
	}
	console.log('Final utilization calculated:', utilization);

 	// Return value between 0 and infinity (no upper limit)
 	return Math.max(0, utilization);
	}

	// Helper method to process all chargers at once if needed
	private calculateAllUtilizations(): Array<{chargerId: number, utilization: number}> {
		if (!this.data?.list) return [];

		return this.data.list.map(charger => ({
			chargerId: charger.chargerid,
			utilization: this.calculateUtilization()
		}));
	}

	eventEmit(event: {type: string, data: any}) {
		console.log('Event received:', event.type, event.data);

		switch (event.type) {
			case "filteredData":
				console.log('=== Filtered Data Event ===');
				if (event.data?.period) {
					this.selectedPeriod = event.data.period;
					console.log('Period updated from filtered data:', this.selectedPeriod);
				}
				this.filteredData = event.data;
				const filteredUtilization = this.calculateUtilization();
				this.chartData = { list: filteredUtilization };
				break;

			case "periodChange":
				console.log('=== Period Change Event ===');
				console.log('Previous period:', this.selectedPeriod);
				this.selectedPeriod = event.data;
				console.log('Period updated to:', this.selectedPeriod);

				// Update filtered data based on new period
				if (this.data?.list) {
					const newFilteredData = {
						period: event.data,
						list: this.data.list.map(device => {
							const deviceUtilization = this.calculateUtilization();
							return {
								...device,
								value: deviceUtilization
							};
						})
					};
					this.filteredData = newFilteredData;
					console.log('Data updated for period:', this.selectedPeriod);
				}

				// Update chart data
				const periodUtilization = this.calculateUtilization();
				this.chartData = { list: periodUtilization };
				break;
			case 'export':
				this.handleExport(event.data);
				break;
		}
	}

	private handleExport(eventData: any) {
		// Flatten the filtered data into a single array
		const flattenedData = this.filteredData.reduce((acc: any[], group: any) => {
			if (group.list && Array.isArray(group.list)) {
				return acc.concat(group.list);
			}
			return acc;
		}, []);

		let exportFilters = this.commonUtil.getExportWidgetFiltersInfo(eventData);
		let csvData: any = [
			{ a: `Site: ${this.customerName}/${this.siteName}` }
		];

		// Add filters info
		for(let key in exportFilters) {
			csvData.push({a: exportFilters[key]});
		}

		// Add metadata
		csvData.push(
			{a: `Date of print: ${moment().format('LL')}`},
			{a: `Period: ${this.getPeriodDisplay(this.selectedPeriod)}`},
			{} // Empty row
		);

		// Add headers
		csvData.push({
			a: "Charger Id",
			b: "Charger SN",
			c: "Utilization %",
			d: "Expected Cycles",
			e: "Actual Cycles",
			f: "Time Period"
		});

		// If no data is found, add a default row with zeros
		if (!flattenedData || flattenedData.length === 0) {
			csvData.push({
				a: "No Data",
				b: "N/A",
				c: "0.00",
				d: "0",
				e: "0",
				f: this.getPeriodDisplay(this.selectedPeriod)
			});
		} else {
			// Process each device from the flattened filtered data
			flattenedData.forEach((device: any) => {
				csvData.push({
					a: device.chargerusername || device.chargerid || 'Unknown',
					b: device.serialnumber || 'N/A',
					c: typeof device.value === 'string' ? Number(device.value).toFixed(2) : device.value.toFixed(2),
					d: this.getExpectedCycles(device.chargerid),
					e: this.getActualCycles(device.chargerid),
					f: this.getPeriodDisplay(this.selectedPeriod)
				});
			});
		}

		let fileName = `Charger_Utilization_${this.customerName}_${this.siteName}_${moment().format('YYYY-MM-DD')}`;
		new ngxCsv(csvData, fileName);
	}

	// Helper methods to get cycles data
	private getExpectedCycles(chargerId: number): number {
		const period = this.selectedPeriod;
		// Iterate through all site IDs to find the charger
		if (!this.data?.plugins?.[period]) return 0;

		let expectedCycles = 0;
		// Loop through all site IDs in the plugins data
		Object.keys(this.data.plugins[period]).forEach(siteId => {
			// Check if this site has the charger
			if (this.data.plugins[period][siteId]?.[chargerId]) {
				expectedCycles = this.data.plugins[period][siteId][chargerId];
			}
		});

		return expectedCycles;
	}

	private getActualCycles(chargerId: number): number {
		const period = this.selectedPeriod;
		const cycleData = this.data.count_of_cycles?.[period]?.find((c: any) =>
			c.chargerid === chargerId
		);
		return cycleData?.count || 0;
	}

	// Helper method to display period in a user-friendly format
	private getPeriodDisplay(period: string): string {
		switch (period) {
			case 'Y':
				return 'Yesterday';
			case 'W':
				return 'Last Week';
			case '7':
				return 'Last 7 Days';
			case '14':
				return 'Last 14 Days';
			case '30':
				return 'Last 30 Days';
			case '90':
				return 'Last 90 Days';
			default:
				return `Last ${period} Days`;
		}
	}
}
